import { useEffect, useState } from 'react';

import { hasAnyKey } from '@/auth/writeKeys';
import {
  companySsoLinkLater,
  loginPreferencesLinkLater,
  organisationsLinkLater,
  organisationsLinkNow,
  ssoSettingsByPermission,
} from '@/pages/access-admin/companySso';
import { ROLE_READ } from '@/pages/access-admin/roleCapabilities';
import { accessReviewLink } from '@/pages/access-admin/securityRules';
import { ApiProvider } from '@/provider/ApiProvider';
import { isCustomerContext } from '@/utils/userRole';

/**
 * Settings pages: who may open them (page sweep 2026-10-16, cluster C1).
 *
 * ONE source for both the Settings menu (components/shared/Sidebar/
 * SidebarSettings.jsx: is the entry shown?) and the routes (routes/index.tsx
 * via auth/SettingsGate.tsx: does the page open?). A page whose gate fails is
 * the shared no-permission page and makes none of its calls, so a role never
 * fires a request the server will refuse just by opening an address.
 *
 * Each gate is decided from the active role (context type and permission map)
 * at once (`now`); the few that also admit platform owners ask the identity
 * service once per active assignment (`later`, effective-access, read only by
 * a role the service answers: pages/access-admin/companySso.ts). Every rule
 * mirrors the server's; the server decides every call again.
 */

const has = (key: string): boolean => Boolean(ApiProvider.permission(key));

interface SettingsGateRule {
  /** decided from the active role and its permission map, without a call */
  now: () => boolean;
  /** asked only when `now` is false (one effective-access read per assignment) */
  later?: () => Promise<boolean>;
}

const emailTemplates = (): boolean => isCustomerContext() && has('view_email_template');

export const SETTINGS_GATES = {
  /** DBS > Currency Exchange Rates `/currencyexchange` */
  currencyExchange: { now: () => has('currency_exchange_rate_list') },
  /** Manage Users > Roles `/userrolelist` */
  roles: { now: () => has('role_list') },
  /**
   * Roles > Permissions `/rolepermission/alltype/:id`: permission_create (the
   * page's own rule; it is one of the role read keys the role lookup needs).
   */
  rolePermissions: { now: () => has('permission_create') },
  /** Roles > Tiles `/rolepermission/tilestype/:id`: tiles_create and a role read key. */
  roleTiles: { now: () => has('tiles_create') && hasAnyKey(ROLE_READ, has) },
  /** Manage Users > All Users `/userlist` and a user `/users/:id` (GET admin/users: user_list) */
  users: { now: () => has('user_list') },
  /** Add user `/users/new` (POST admin/users: user_create) */
  userCreate: { now: () => has('user_create') },
  /** Manage Users > Organisations `/organisations` */
  organisations: { now: organisationsLinkNow, later: organisationsLinkLater },
  /** Manage Users > Access review `/access-review` */
  accessReview: { now: accessReviewLink },
  /** Manage Users > Company sign-in (SSO) `/company-sso`: platform owners in an internal role */
  companySso: { now: () => false, later: companySsoLinkLater },
  /** Your organization > Login Preferences `/loginpreferences` */
  loginPreferences: { now: ssoSettingsByPermission, later: loginPreferencesLinkLater },
  /** Your organization > Email Templates `/emailtemplates` (a customer context) */
  emailTemplates: { now: emailTemplates },
  /** Create an e-mail template `/addemailtemplate` */
  emailTemplateCreate: { now: () => emailTemplates() && has('create_email_template') },
  /** Edit an e-mail template `/emailtemplates/:id/edit` */
  emailTemplateEdit: { now: () => emailTemplates() && has('edit_email_template') },
} satisfies Record<string, SettingsGateRule>;

export type SettingsGateName = keyof typeof SETTINGS_GATES;

const rule = (name: SettingsGateName): SettingsGateRule => SETTINGS_GATES[name];

/** The gate as far as it is known without a call. */
export function settingsGateNow(name: SettingsGateName): boolean {
  return rule(name).now();
}

/** The gate, asking the identity service when `now` does not admit (never rejects). */
export function settingsGateLater(name: SettingsGateName): Promise<boolean> {
  const r = rule(name);
  if (r.now()) return Promise.resolve(true);
  if (!r.later) return Promise.resolve(false);
  return r.later().then(
    (v) => v === true,
    () => false,
  );
}

/**
 * React: the gate's answer; null while it is being asked or while `enabled` is
 * false (nothing is asked then). Re-decided on every render, so a permission
 * map that arrives later opens the gate.
 */
export function useSettingsGate(name: SettingsGateName, enabled = true): boolean | null {
  const now = enabled && settingsGateNow(name);
  const hasLater = Boolean(rule(name).later);
  const [later, setLater] = useState<{ name: SettingsGateName; value: boolean } | null>(null);

  useEffect(() => {
    if (!enabled || now || !hasLater) return undefined;
    let alive = true;
    void settingsGateLater(name).then((value) => {
      if (alive) setLater({ name, value });
    });
    return () => {
      alive = false;
    };
  }, [enabled, now, hasLater, name]);

  if (!enabled) return null;
  if (now) return true;
  if (!hasLater) return false;
  return later?.name === name ? later.value : null;
}

/** A Settings menu entry: shown once its gate admits; asked only while `enabled`. */
export function useSettingsLink(name: SettingsGateName, enabled: boolean): boolean {
  return useSettingsGate(name, enabled) === true;
}
